/**
 * Typography preferences, as steps of the project's own scales.
 *
 * The panel writes nothing but the four CSS variables index.css already reads,
 * and every value it writes is a `var()` pointing at a token: no number is
 * invented here. Size climbs the φ^¼ type ladder, leading climbs the same
 * ladder in quarter-steps of φ, and the measure is Fibonacci counted in
 * characters. Three settings, three scales, zero free numbers.
 *
 * Persisted, because a reader adjusts the measure once and expects never to do
 * it again.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

import { CloseIcon } from "../components/icons";
import { IconButton } from "../components/ui";
import { t, useUiLang } from "../i18n";

export interface ReadingPrefs {
  /** Index into SIZE_STEPS. */
  sizeStep: number;
  /** Index into LEAD_STEPS. */
  leadStep: number;
  /** Index into MEASURE_STEPS. */
  measureStep: number;
  font: "serif" | "sans";
}

/**
 * The rungs a reader may stand on. The pixel figure beside each token is only
 * there to be shown in the panel: it duplicates index.css on purpose, because
 * a stylesheet variable cannot be read back without laying out an element, and
 * a settings panel must not depend on the document it is styling. Two steps
 * make √φ, four steps make φ exactly.
 */
const SIZE_STEPS = [
  { token: "--text-base", px: 15 },
  { token: "--text-lg", px: 17 },
  { token: "--text-xl", px: 19 }, // 12φ: the default, one golden ratio above the chrome
  { token: "--text-2xl", px: 22 },
  { token: "--text-3xl", px: 25 },
  { token: "--text-4xl", px: 28 },
  { token: "--text-5xl", px: 31 },
] as const;

const LEAD_STEPS = [
  { token: "--reading-lead-tight", labelKey: "prefsLeadTight" },
  { token: "--reading-lead-normal", labelKey: "prefsLeadNormal" },
  { token: "--reading-lead-loose", labelKey: "prefsLeadLoose" },
] as const;

const MEASURE_STEPS = [
  { token: "--reading-measure-narrow", labelKey: "prefsMeasureNarrow", chars: 34 },
  { token: "--reading-measure-normal", labelKey: "prefsMeasureNormal", chars: 55 },
  { token: "--reading-measure-wide", labelKey: "prefsMeasureWide", chars: 89 },
] as const;

const DEFAULTS: ReadingPrefs = { sizeStep: 2, leadStep: 1, measureStep: 1, font: "serif" };
const STORAGE_KEY = "readcommons.reading";

function clampStep(value: unknown, count: number, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(count - 1, Math.max(0, Math.round(value)));
}

/** The shape written before the scales existed: three free numbers, a size and
 * a measure in rem and a bare line height. It is snapped to the nearest rung
 * rather than discarded, so nobody's settings silently reset under them. */
function migrate(value: Record<string, unknown>): ReadingPrefs | null {
  const { size, leading, measure } = value;
  if (typeof size !== "number" && typeof leading !== "number" && typeof measure !== "number") {
    return null;
  }
  const px = typeof size === "number" ? size * 16 : SIZE_STEPS[DEFAULTS.sizeStep].px;
  let sizeStep = 0;
  for (let i = 1; i < SIZE_STEPS.length; i++) {
    if (Math.abs(SIZE_STEPS[i].px - px) < Math.abs(SIZE_STEPS[sizeStep].px - px)) sizeStep = i;
  }
  const lead = typeof leading === "number" ? leading : 1.618;
  const leadStep = lead < 1.53 ? 0 : lead < 1.72 ? 1 : 2;
  // The old measure was in rem of a column, the new one in characters of the
  // running text. There is no exact conversion between them; these two cuts
  // land a 34rem default on the middle rung, which is where it looked.
  const rem = typeof measure === "number" ? measure : 34;
  const measureStep = rem <= 30 ? 0 : rem <= 44 ? 1 : 2;
  return { sizeStep, leadStep, measureStep, font: value.font === "sans" ? "sans" : "serif" };
}

function load(): ReadingPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULTS;
    const value = parsed as Record<string, unknown>;
    const migrated = migrate(value);
    if (migrated) return migrated;
    return {
      sizeStep: clampStep(value.sizeStep, SIZE_STEPS.length, DEFAULTS.sizeStep),
      leadStep: clampStep(value.leadStep, LEAD_STEPS.length, DEFAULTS.leadStep),
      measureStep: clampStep(value.measureStep, MEASURE_STEPS.length, DEFAULTS.measureStep),
      font: value.font === "sans" ? "sans" : "serif",
    };
  } catch {
    // A corrupt or unavailable localStorage is not a reason to fail to open a book.
    return DEFAULTS;
  }
}

export function useReadingPrefs(): {
  prefs: ReadingPrefs;
  update: (change: Partial<ReadingPrefs>) => void;
  reset: () => void;
} {
  const [prefs, setPrefs] = useState<ReadingPrefs>(load);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Private browsing. The session still works, it just will not be remembered.
    }
  }, [prefs]);

  const update = useCallback((change: Partial<ReadingPrefs>) => {
    setPrefs((current) => ({ ...current, ...change }));
  }, []);

  const reset = useCallback(() => setPrefs(DEFAULTS), []);
  return { prefs, update, reset };
}

/** The variables `.reading` in index.css already expects, each one pointing at
 * a token rather than carrying a value of its own. */
export function readingVars(prefs: ReadingPrefs): CSSProperties {
  return {
    "--reading-size": `var(${SIZE_STEPS[prefs.sizeStep].token})`,
    "--reading-leading": `var(${LEAD_STEPS[prefs.leadStep].token})`,
    "--reading-measure": `var(${MEASURE_STEPS[prefs.measureStep].token})`,
  } as CSSProperties;
}

/** A row of named rungs. Used for everything that has three of them: nothing
 * on this panel is a continuum, so nothing on it is a free slider. */
function Steps<T extends string | number>({
  label,
  options,
  value,
  onChange,
  serifPreview,
}: {
  label: string;
  options: readonly { value: T; text: string }[];
  value: T;
  onChange: (value: T) => void;
  serifPreview?: boolean;
}) {
  return (
    <div>
      <span className="text-xs text-[var(--color-muted)]">{label}</span>
      <div
        role="radiogroup"
        aria-label={label}
        className={
          "mt-2 grid gap-1 rounded-lg bg-[var(--color-surface)] p-1 " +
          // Written out rather than interpolated: Tailwind scans this file as
          // text, and a class it cannot read as a literal is never generated.
          (options.length === 2 ? "grid-cols-2" : "grid-cols-3")
        }
      >
        {options.map((option) => (
          <button
            key={String(option.value)}
            role="radio"
            aria-checked={value === option.value}
            onClick={() => onChange(option.value)}
            className={
              "tap flex items-center justify-center rounded-md px-2 py-1.5 text-sm transition-colors duration-150 " +
              (value === option.value
                ? "bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
                : "text-[var(--color-muted)] hover:text-[var(--color-ink)]")
            }
            style={
              serifPreview
                ? { fontFamily: option.value === "serif" ? "var(--font-read)" : "var(--font-ui)" }
                : undefined
            }
          >
            {option.text}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The one setting with seven rungs rather than three. A slider, but a
 * detented one: `step={1}` walks the ladder, it does not slide between it. */
function SizeSteps({
  label,
  step,
  onChange,
}: {
  label: string;
  step: number;
  onChange: (step: number) => void;
}) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between text-xs text-[var(--color-muted)]">
        {label}
        <span className="font-mono text-2xs text-[var(--color-faint)]">
          {t("prefsPixels", { n: SIZE_STEPS[step].px })}
        </span>
      </span>
      <input
        type="range"
        min={0}
        max={SIZE_STEPS.length - 1}
        step={1}
        value={step}
        aria-valuetext={t("prefsPixels", { n: SIZE_STEPS[step].px })}
        onChange={(event) => onChange(Number(event.target.value))}
        className={
          // The track stays a hairline; under a thumb the control itself gets a
          // 44px band to be grabbed by, drawn transparent above and below.
          "mt-2 h-1 w-full cursor-pointer touch-none appearance-none rounded-full bg-[var(--color-line)] " +
          "coarse:h-11 coarse:bg-transparent coarse:bg-[linear-gradient(var(--color-line),var(--color-line))] " +
          "coarse:bg-[length:100%_4px] coarse:bg-center coarse:bg-no-repeat " +
          "coarse:[&::-webkit-slider-thumb]:h-6 coarse:[&::-webkit-slider-thumb]:w-6 " +
          "coarse:[&::-moz-range-thumb]:h-6 coarse:[&::-moz-range-thumb]:w-6 " +
          "[&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 " +
          "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full " +
          "[&::-webkit-slider-thumb]:bg-[var(--color-accent)] " +
          "[&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:border-0 " +
          "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[var(--color-accent)]"
        }
      />
    </label>
  );
}

export function ReadingPrefsPanel({
  prefs,
  onChange,
  onReset,
  onClose,
}: {
  prefs: ReadingPrefs;
  onChange: (change: Partial<ReadingPrefs>) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  useUiLang();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (!panel.current?.contains(event.target as Node)) onClose();
    };
    // Deferred: the click that opened the panel must not immediately close it.
    const timer = window.setTimeout(() => document.addEventListener("mousedown", away), 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", away);
    };
  }, [onClose]);

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={t("readerPreferences")}
      className={
        "absolute right-0 top-full z-30 mt-2 w-72 max-w-[calc(100vw-1.5rem)] rounded-xl bg-[var(--color-raised)] p-4 " +
        "shadow-2xl shadow-black/50 ring-1 ring-[var(--color-line)]"
      }
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-medium text-[var(--color-ink)]">{t("prefsTitle")}</h2>
        <IconButton
          label={t("prefsClose")}
          onClick={onClose}
          className="h-7 w-7 coarse:h-11 coarse:w-11"
        >
          <CloseIcon className="h-4 w-4" />
        </IconButton>
      </div>

      <div className="grid gap-4">
        <SizeSteps
          label={t("prefsTextSize")}
          step={prefs.sizeStep}
          onChange={(sizeStep) => onChange({ sizeStep })}
        />

        <Steps
          label={t("prefsLineHeight")}
          value={prefs.leadStep}
          options={LEAD_STEPS.map((lead, index) => ({ value: index, text: t(lead.labelKey) }))}
          onChange={(leadStep) => onChange({ leadStep })}
        />

        <Steps
          label={t("prefsMeasure")}
          value={prefs.measureStep}
          options={MEASURE_STEPS.map((measure, index) => ({
            value: index,
            text: t(measure.labelKey),
          }))}
          onChange={(measureStep) => onChange({ measureStep })}
        />

        <p className="text-2xs text-[var(--color-faint)]">
          {t("prefsMeasureChars", { n: MEASURE_STEPS[prefs.measureStep].chars })}
        </p>

        <Steps
          label={t("prefsTypeface")}
          value={prefs.font}
          options={[
            { value: "serif" as const, text: t("prefsSerif") },
            { value: "sans" as const, text: t("prefsSans") },
          ]}
          onChange={(font) => onChange({ font })}
          serifPreview
        />

        <button
          onClick={onReset}
          className="tap justify-self-start text-xs text-[var(--color-faint)] hover:text-[var(--color-muted)]"
        >
          {t("prefsReset")}
        </button>
      </div>
    </div>
  );
}
